import { Dispatch, RefObject, SetStateAction, useCallback, useRef, useState } from "react";

export type UseStateWithRefReturn<T> = [state: T, ref: RefObject<T>, setValue: Dispatch<SetStateAction<T>>];

/**
 * This hook returns state, ref and setter for both of them.
 * Ref can be used for chained updates, where next value depends on previous.
 * This is possible with useState and function setter, but with useStateWithRef
 * you can also read up-to-date value for API-calls etc.
 *
 * Always use ref-value in procedural code to get up-to-date value.
 * Always use state-value for rendering.
 */
export function useStateWithRef<T>(initialValue: T): UseStateWithRefReturn<T> {
	const ref = useRef<T>(initialValue);
	const [state, setState] = useState<T>(initialValue);
	const setValue = useCallback(
		(valueOrFunction: SetStateAction<T>) => {
			if (valueOrFunction instanceof Function) {
				setState((previous) => {
					const newValue = valueOrFunction(previous);
					ref.current = newValue;
					return newValue;
				});
			} else {
				ref.current = valueOrFunction;
				setState(valueOrFunction);
			}
		},
		[setState],
	);

	return [state, ref, setValue];
}
